import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  Sheet,
  IconButton,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Stack,
  Alert,
  LinearProgress,
  Tooltip,
} from '@mui/joy';
import {
  Plus as AddIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  TrendUp as TrendingUpIcon,
  TrendDown as TrendingDownIcon,
  MagnifyingGlass as SearchIcon,
  Star as StarIcon,
  Star as StarBorderIcon,
} from '@phosphor-icons/react';
import { useKeywords, useCreateKeyword, useUpdateKeyword, useDeleteKeyword } from '../../hooks/useKeywords';
import type { LocalKeyword } from '../../types/marketing';

interface KeywordFormData {
  keyword: string;
  location: string;
  target_page: string;
  is_primary: boolean;
  current_rank?: number | null;
  search_volume?: number | null;
  difficulty?: number | null;
}

export const KeywordsManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<LocalKeyword | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<KeywordFormData>({
    keyword: '',
    location: '',
    target_page: '',
    is_primary: false,
    current_rank: null,
    search_volume: null,
    difficulty: null,
  });

  const { data: keywords = [], isLoading, error } = useKeywords();
  const createMutation = useCreateKeyword();
  const updateMutation = useUpdateKeyword();
  const deleteMutation = useDeleteKeyword();

  const handleOpenModal = (keyword?: LocalKeyword) => {
    if (keyword) {
      setEditingKeyword(keyword);
      setFormData({
        keyword: keyword.keyword,
        location: keyword.location,
        target_page: keyword.target_page,
        is_primary: keyword.is_primary,
        current_rank: keyword.current_rank,
        search_volume: keyword.search_volume,
        difficulty: keyword.difficulty,
      });
    } else {
      setEditingKeyword(null);
      setFormData({
        keyword: '',
        location: '',
        target_page: '',
        is_primary: false,
        current_rank: null,
        search_volume: null,
        difficulty: null,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingKeyword(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingKeyword) {
        await updateMutation.mutateAsync({
          id: editingKeyword.id,
          ...formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save keyword:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this keyword?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete keyword:', error);
      }
    }
  };

  const getRankIcon = (keyword: LocalKeyword) => {
    if (!keyword.rank_change || keyword.rank_change === 0) return null;

    if (keyword.rank_change > 0) {
      return (
        <Chip
          variant="soft"
          color="success"
          size="sm"
          startDecorator={<TrendingUpIcon />}
        >
          +{keyword.rank_change}
        </Chip>
      );
    } else {
      return (
        <Chip
          variant="soft"
          color="danger"
          size="sm"
          startDecorator={<TrendingDownIcon />}
        >
          {keyword.rank_change}
        </Chip>
      );
    }
  };

  const getDifficultyColor = (difficulty: number | null) => {
    if (!difficulty) return 'neutral';
    if (difficulty < 30) return 'success';
    if (difficulty < 70) return 'warning';
    return 'danger';
  };

  const filteredKeywords = Array.isArray(keywords)
    ? keywords.filter((keyword: LocalKeyword) =>
        keyword.keyword.toLowerCase().includes(searchTerm.toLowerCase()) ||
        keyword.location.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  if (error) {
    return (
      <Alert color="danger">
        Failed to load keywords. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="h3">Keyword Tracking</Typography>
        <Button
          startDecorator={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Add Keyword
        </Button>
      </Box>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <CollapsibleFilters><Input
              placeholder="Search keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startDecorator={<SearchIcon />}
              sx={{ flex: 1 }}
            /></CollapsibleFilters>
          </Box>

          {isLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading keywords...</Typography>
            </Box>
          ) : filteredKeywords.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="neutral" sx={{ mb: 2 }}>
                {searchTerm ? 'No keywords found matching your search.' : 'No keywords tracked yet.'}
              </Typography>
              {!searchTerm && (
                <Button
                  variant="soft"
                  startDecorator={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Track Your First Keyword
                </Button>
              )}
            </Box>
          ) : (
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
              <Table hoverRow stickyHeader>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Keyword</th>
                    <th>Location</th>
                    <th style={{ textAlign: 'center' }}>Current Rank</th>
                    <th style={{ textAlign: 'center' }}>Change</th>
                    <th style={{ textAlign: 'center' }}>Search Volume</th>
                    <th style={{ textAlign: 'center' }}>Difficulty</th>
                    <th>Target Page</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKeywords.map((keyword: LocalKeyword) => (
                    <tr key={keyword.id}>
                      <td>
                        <Tooltip title={keyword.is_primary ? 'Primary keyword' : 'Secondary keyword'}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color={keyword.is_primary ? 'warning' : 'neutral'}
                            onClick={() => updateMutation.mutate({
                              id: keyword.id,
                              is_primary: !keyword.is_primary,
                            })}
                          >
                            {keyword.is_primary ? <StarIcon /> : <StarBorderIcon />}
                          </IconButton>
                        </Tooltip>
                      </td>
                      <td>
                        <Typography level="body-sm" fontWeight="md">
                          {keyword.keyword}
                        </Typography>
                      </td>
                      <td>{keyword.location}</td>
                      <td style={{ textAlign: 'center' }}>
                        {keyword.current_rank ? (
                          <Chip
                            variant="soft"
                            color={keyword.current_rank <= 10 ? 'success' : 'neutral'}
                            size="sm"
                          >
                            #{keyword.current_rank}
                          </Chip>
                        ) : (
                          <Typography level="body-sm" color="neutral">-</Typography>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {getRankIcon(keyword)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {keyword.search_volume ? (
                          <Typography level="body-sm">{keyword.search_volume.toLocaleString()}</Typography>
                        ) : (
                          <Typography level="body-sm" color="neutral">-</Typography>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {keyword.difficulty ? (
                          <Chip
                            variant="soft"
                            color={getDifficultyColor(keyword.difficulty)}
                            size="sm"
                          >
                            {keyword.difficulty}
                          </Chip>
                        ) : (
                          <Typography level="body-sm" color="neutral">-</Typography>
                        )}
                      </td>
                      <td>
                        {keyword.target_page && (
                          <Typography
                            level="body-sm"
                            component="a"
                            href={keyword.target_page}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: 'none',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {keyword.target_page.length > 40
                              ? `${keyword.target_page.substring(0, 40)}...`
                              : keyword.target_page}
                          </Typography>
                        )}
                      </td>
                      <td>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => handleOpenModal(keyword)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleDelete(keyword.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          )}
        </CardContent>
      </Card>

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <ModalDialog sx={{ width: 500 }}>
          <ModalClose />
          <Typography level="h4">
            {editingKeyword ? 'Edit Keyword' : 'Add New Keyword'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <FormControl required>
                <FormLabel>Keyword</FormLabel>
                <Input
                  value={formData.keyword}
                  onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                  placeholder="e.g., roof repair"
                />
              </FormControl>

              <FormControl required>
                <FormLabel>Location</FormLabel>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Denver, CO"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Target Page URL</FormLabel>
                <Input
                  value={formData.target_page}
                  onChange={(e) => setFormData({ ...formData, target_page: e.target.value })}
                  placeholder="https://example.com/services/roof-repair"
                />
              </FormControl>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                <FormControl>
                  <FormLabel>Current Rank</FormLabel>
                  <Input
                    type="number"
                    value={formData.current_rank || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      current_rank: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="1-100"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Search Volume</FormLabel>
                  <Input
                    type="number"
                    value={formData.search_volume || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      search_volume: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="1000"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Difficulty</FormLabel>
                  <Input
                    type="number"
                    value={formData.difficulty || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      difficulty: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="0-100"
                  />
                </FormControl>
              </Box>

              <FormControl>
                <Checkbox
                  label="Primary keyword"
                  checked={formData.is_primary}
                  onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button variant="plain" color="neutral" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                  {editingKeyword ? 'Update' : 'Add'} Keyword
                </Button>
              </Box>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
